import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DashboardLayout } from "@/components/DashboardLayout";
import { KCard } from "@/components/KCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Award, Download, Printer, Eye, Check, Loader2, Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { getCourses, type EnrolledStudentRow } from "@/api/lms/courses";
import {
  getMyCertificates,
  getAllCertificates,
  issueCertificate,
  getCertificateCourseStudents,
  searchCertificateUsers,
  getCertificateUserEnrollments,
  type CertificateDto,
  type CertificateStudentSearchHit,
} from "@/api/lms/certificates";
import type { ApiError } from "@/auth/apiClient";
import {
  drawCertificate,
  setupCertificateCanvas,
  downloadCertificatePng,
  openCertificatePrintWindow,
  certificateAspectRatioStyle,
  loadCertificateLogo,
  loadMehulTemplates,
  isMehulTemplate,
  type CertificateVisualModel,
} from "@/lib/certificateCanvas";

const templates = [
  { id: "classic" as const,  name: "Classic",    color: "from-primary/5 to-primary/10",  border: "border-primary/20",  thumbSrc: null as string | null },
  { id: "elegant" as const,  name: "Elegant",    color: "from-warning/5 to-warning/10",  border: "border-warning/20",  thumbSrc: null as string | null },
  { id: "modern" as const,   name: "Modern",     color: "from-success/5 to-success/10",  border: "border-success/20",  thumbSrc: null as string | null },
  { id: "mehul-1" as const,  name: "Template 1", color: "from-red-50 to-amber-50",       border: "border-red-200",     thumbSrc: "/certificate_template_1.jpeg" },
  { id: "mehul-2" as const,  name: "Template 2", color: "from-amber-50 to-yellow-50",   border: "border-amber-200",   thumbSrc: "/certificate_template_2.jpeg" },
];

const DEFAULT_INSTITUTION = "MEHUL Japanese Language Education Center";
const DEFAULT_SUBTITLE = "Certificate of Completion";

function formatIssuedDate(iso: string): string {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function displayStudentNameFromRow(s: EnrolledStudentRow): string {
  const fn = s.user.profile?.firstName ?? "";
  const ln = s.user.profile?.lastName ?? "";
  const full = `${fn} ${ln}`.trim();
  return full || s.user.email;
}

function templateTitle(id: string): string {
  return templates.find((t) => t.id === id)?.name ?? id;
}

function templateThumb(id: string): string | null {
  return templates.find((t) => t.id === id)?.thumbSrc ?? null;
}

function certDtoToVisual(cert: CertificateDto): CertificateVisualModel {
  const t = cert.template;
  type Tpl = CertificateVisualModel["template"];
  const validTemplates: Tpl[] = ["classic", "elegant", "modern", "mehul-1", "mehul-2"];
  const tpl: Tpl = (validTemplates.includes(t as Tpl) ? t : "classic") as Tpl;
  return {
    template: tpl,
    institutionName: cert.institutionName?.trim() || DEFAULT_INSTITUTION,
    subtitle: cert.subtitle?.trim() || DEFAULT_SUBTITLE,
    recipientName:
      cert.recipientDisplayName?.trim() || cert.recipientName?.trim() || cert.studentName?.trim() || "Student",
    courseTitle: cert.courseDisplayTitle?.trim() || cert.courseTitle,
    grade: cert.grade,
    certificateNumber: cert.certificateNumber,
    issuedDateLabel: formatIssuedDate(cert.issuedAt),
    signatoryTitle: cert.signatoryTitle?.trim() || null,
    // Mehul-specific
    studentId: cert.studentId?.trim() || undefined,
    dateOfBirth: cert.dateOfBirth?.trim() || undefined,
    gender: cert.gender?.trim() || undefined,
    totalLessonHours: cert.totalLessonHours?.trim() || undefined,
    attendance: cert.attendance?.trim() || undefined,
    textBook: cert.textBook?.trim() || undefined,
  };
}

function CertificatePreviewCanvas({ model }: { model: CertificateVisualModel }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const portrait = isMehulTemplate(model.template);

  useLayoutEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    let cancelled = false;
    const render = () => {
      const c = ref.current;
      if (!c) return;
      const s = setupCertificateCanvas(c, portrait);
      if (!s) return;
      drawCertificate(s.ctx, s.w, s.h, model);
    };
    render();
    if (portrait) {
      void loadMehulTemplates().then(() => {
        if (cancelled) return;
        render();
      });
    } else {
      void loadCertificateLogo().then((img) => {
        if (cancelled || !img) return;
        render();
      });
    }
    return () => { cancelled = true; };
  }, [model, portrait]);

  return (
    <div
      className="w-full overflow-hidden rounded-lg border border-border bg-white shadow-lg"
      style={certificateAspectRatioStyle(portrait)}
    >
      <canvas ref={ref} className="block size-full" />
    </div>
  );
}

function CertificateCardActions({ cert }: { cert: CertificateDto }) {
  const model = useMemo(() => certDtoToVisual(cert), [cert]);

  const print = useCallback(async () => {
    const ok = await openCertificatePrintWindow(model);
    if (!ok) toast.error("Allow pop-ups to print.");
  }, [model]);

  const png = useCallback(() => {
    void downloadCertificatePng(model, `certificate-${cert.certificateNumber}`);
  }, [model, cert.certificateNumber]);

  return (
    <div className="flex gap-2">
      <Button size="sm" variant="ghost" type="button" title="Print" onClick={print}>
        <Eye size={14} />
      </Button>
      <Button size="sm" variant="secondary" type="button" title="Download PNG" onClick={png}>
        <Download size={14} />
      </Button>
    </div>
  );
}

function previewGradient(templateId: string): string {
  if (templateId === "classic") return "from-primary/5 to-primary/10";
  if (templateId === "elegant") return "from-warning/5 to-warning/10";
  return "from-success/5 to-success/10";
}

type PickMode = "roster" | "search";

export default function CertificatesPage() {
  const { pathname } = useLocation();
  const isAdminCertificatesRoute = pathname.startsWith("/admin/certificates");
  const queryClient = useQueryClient();

  const [pickMode, setPickMode] = useState<PickMode>("roster");
  const [selectedTemplate, setSelectedTemplate] = useState<CertificateVisualModel["template"]>("mehul-1");
  const [selectedCourseId, setSelectedCourseId] = useState("");
  const [studentUserId, setStudentUserId] = useState<number | null>(null);
  const [gradeInput, setGradeInput] = useState("");
  const [recipientDisplayName, setRecipientDisplayName] = useState("");
  const [courseDisplayTitle, setCourseDisplayTitle] = useState("");
  const [institutionName, setInstitutionName] = useState(DEFAULT_INSTITUTION);
  const [subtitle, setSubtitle] = useState(DEFAULT_SUBTITLE);
  const [signatoryTitle, setSignatoryTitle] = useState("");
  const [rosterFilter, setRosterFilter] = useState("");
  const [globalSearchInput, setGlobalSearchInput] = useState("");
  const [debouncedGlobalSearch, setDebouncedGlobalSearch] = useState("");
  const [selectedSearchHit, setSelectedSearchHit] = useState<CertificateStudentSearchHit | null>(null);
  // Mehul-template–specific fields
  const [studentId, setStudentId] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [gender, setGender] = useState("");
  const [totalLessonHours, setTotalLessonHours] = useState("");
  const [attendance, setAttendance] = useState("");
  const [textBook, setTextBook] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebouncedGlobalSearch(globalSearchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [globalSearchInput]);

  useEffect(() => {
    void loadCertificateLogo();
    void loadMehulTemplates();
  }, []);

  const myCertsQuery = useQuery({
    queryKey: ["certificates", "me"],
    queryFn: getMyCertificates,
    enabled: !isAdminCertificatesRoute,
  });

  const adminCertsQuery = useQuery({
    queryKey: ["certificates", "all"],
    queryFn: getAllCertificates,
    enabled: isAdminCertificatesRoute,
  });

  const coursesQuery = useQuery({
    queryKey: ["courses", "admin-certificates"],
    queryFn: () => getCourses({ page: 1, limit: 100 }),
    enabled: isAdminCertificatesRoute,
  });

  const courses = coursesQuery.data?.items ?? [];

  useEffect(() => {
    if (!isAdminCertificatesRoute || coursesQuery.isLoading || pickMode !== "roster") return;
    const list = coursesQuery.data?.items ?? [];
    if (list.length === 0) {
      setSelectedCourseId("");
      return;
    }
    setSelectedCourseId((prev) => (prev && list.some((c) => c.id === prev) ? prev : list[0].id));
  }, [isAdminCertificatesRoute, coursesQuery.isLoading, coursesQuery.data, pickMode]);

  const studentsQuery = useQuery({
    queryKey: ["certificates", "course-students", selectedCourseId],
    queryFn: () => getCertificateCourseStudents(selectedCourseId),
    enabled: isAdminCertificatesRoute && pickMode === "roster" && !!selectedCourseId,
  });

  const students = studentsQuery.data ?? [];

  const userSearchQuery = useQuery({
    queryKey: ["certificates", "user-search", debouncedGlobalSearch],
    queryFn: () => searchCertificateUsers(debouncedGlobalSearch, 25),
    enabled: isAdminCertificatesRoute && debouncedGlobalSearch.length >= 2,
  });

  const enrollmentsQuery = useQuery({
    queryKey: ["certificates", "user-enrollments", studentUserId, pickMode],
    queryFn: () => getCertificateUserEnrollments(studentUserId!),
    enabled: isAdminCertificatesRoute && pickMode === "search" && studentUserId != null && !!selectedSearchHit,
  });

  const enrollmentCourses = enrollmentsQuery.data ?? [];

  useEffect(() => {
    if (!isAdminCertificatesRoute || pickMode !== "roster") return;
    const list = studentsQuery.data ?? [];
    if (list.length === 0) {
      setStudentUserId(null);
      return;
    }
    setStudentUserId((prev) =>
      prev != null && list.some((s) => s.userId === prev) ? prev : list[0].userId,
    );
  }, [isAdminCertificatesRoute, pickMode, selectedCourseId, studentsQuery.data]);

  useEffect(() => {
    if (!isAdminCertificatesRoute || pickMode !== "search") return;
    if (enrollmentCourses.length === 0) {
      setSelectedCourseId("");
      return;
    }
    setSelectedCourseId((prev) =>
      prev && enrollmentCourses.some((e) => e.courseId === prev) ? prev : enrollmentCourses[0].courseId,
    );
  }, [isAdminCertificatesRoute, pickMode, enrollmentCourses]);

  useEffect(() => {
    if (!isAdminCertificatesRoute || pickMode !== "roster" || !selectedCourseId) return;
    const c = courses.find((x) => x.id === selectedCourseId);
    if (c) setCourseDisplayTitle(c.title);
  }, [isAdminCertificatesRoute, pickMode, selectedCourseId, courses]);

  useEffect(() => {
    if (!isAdminCertificatesRoute || pickMode !== "roster" || studentUserId == null) return;
    const row = students.find((s) => s.userId === studentUserId);
    if (row) setRecipientDisplayName(displayStudentNameFromRow(row));
  }, [isAdminCertificatesRoute, pickMode, studentUserId, students]);

  useEffect(() => {
    if (!isAdminCertificatesRoute || pickMode !== "search" || !selectedSearchHit) return;
    setRecipientDisplayName(selectedSearchHit.displayName);
    setStudentUserId(selectedSearchHit.id);
  }, [isAdminCertificatesRoute, pickMode, selectedSearchHit]);

  useEffect(() => {
    if (!isAdminCertificatesRoute || pickMode !== "search" || !selectedCourseId) return;
    const e = enrollmentCourses.find((x) => x.courseId === selectedCourseId);
    if (e) setCourseDisplayTitle(e.title);
  }, [isAdminCertificatesRoute, pickMode, selectedCourseId, enrollmentCourses]);

  const issueMutation = useMutation({
    mutationFn: issueCertificate,
    onSuccess: () => {
      toast.success("Certificate issued.");
      void queryClient.invalidateQueries({ queryKey: ["certificates", "all"] });
    },
    onError: (e: unknown) => {
      const msg =
        typeof e === "object" && e !== null && "message" in e
          ? String((e as ApiError).message)
          : "Could not issue certificate.";
      toast.error(msg);
    },
  });

  const filteredRosterStudents = useMemo(() => {
    const q = rosterFilter.trim().toLowerCase();
    if (!q) return students;
    return students.filter((s) => {
      const name = displayStudentNameFromRow(s).toLowerCase();
      const email = s.user.email.toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  }, [students, rosterFilter]);

  const rosterSelectOptions = useMemo(() => {
    if (studentUserId == null) return filteredRosterStudents;
    const has = filteredRosterStudents.some((s) => s.userId === studentUserId);
    if (has) return filteredRosterStudents;
    const current = students.find((s) => s.userId === studentUserId);
    return current ? [current, ...filteredRosterStudents] : filteredRosterStudents;
  }, [filteredRosterStudents, students, studentUserId]);

  const adminPreviewModel = useMemo((): CertificateVisualModel => {
    const issuedLabel =
      pickMode === "search" && enrollmentsQuery.isLoading
        ? "…"
        : new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
    return {
      template: selectedTemplate,
      institutionName: institutionName.trim() || DEFAULT_INSTITUTION,
      subtitle: subtitle.trim() || DEFAULT_SUBTITLE,
      recipientName: recipientDisplayName.trim() || "Recipient name",
      courseTitle: courseDisplayTitle.trim() || "Course title",
      grade: gradeInput.trim() || null,
      certificateNumber: "PREVIEW - issued on save",
      issuedDateLabel: issuedLabel,
      signatoryTitle: signatoryTitle.trim() || null,
      // Mehul-specific
      studentId: studentId.trim() || undefined,
      dateOfBirth: dateOfBirth.trim() || undefined,
      gender: gender.trim() || undefined,
      totalLessonHours: totalLessonHours.trim() || undefined,
      attendance: attendance.trim() || undefined,
      textBook: textBook.trim() || undefined,
    };
  }, [
    selectedTemplate,
    institutionName,
    subtitle,
    recipientDisplayName,
    courseDisplayTitle,
    gradeInput,
    signatoryTitle,
    pickMode,
    enrollmentsQuery.isLoading,
    studentId,
    dateOfBirth,
    gender,
    totalLessonHours,
    attendance,
    textBook,
  ]);

  const handlePickMode = (mode: PickMode) => {
    setPickMode(mode);
    setRosterFilter("");
    setGlobalSearchInput("");
    setDebouncedGlobalSearch("");
    setSelectedSearchHit(null);
    setStudentUserId(null);
    setSelectedCourseId("");
  };

  const selectSearchHit = (hit: CertificateStudentSearchHit) => {
    setSelectedSearchHit(hit);
    setStudentUserId(hit.id);
    setRecipientDisplayName(hit.displayName);
  };

  const handleIssue = () => {
    if (!selectedCourseId || studentUserId == null) {
      toast.error("Select a course and student.");
      return;
    }
    const name = recipientDisplayName.trim();
    if (!name) {
      toast.error("Enter the name as it should appear on the certificate.");
      return;
    }
    issueMutation.mutate({
      userId: studentUserId,
      courseId: selectedCourseId,
      template: selectedTemplate,
      recipientDisplayName: name,
      grade: gradeInput.trim() || undefined,
      courseDisplayTitle: courseDisplayTitle.trim() || undefined,
      institutionName: institutionName.trim() || undefined,
      subtitle: subtitle.trim() || undefined,
      signatoryTitle: signatoryTitle.trim() || undefined,
      // Mehul-specific
      studentId: studentId.trim() || undefined,
      dateOfBirth: dateOfBirth.trim() || undefined,
      gender: gender.trim() || undefined,
      totalLessonHours: totalLessonHours.trim() || undefined,
      attendance: attendance.trim() || undefined,
      textBook: textBook.trim() || undefined,
    });
  };

  const handlePrintPreview = async () => {
    const ok = await openCertificatePrintWindow(adminPreviewModel);
    if (!ok) toast.error("Allow pop-ups to print.");
  };

  const handleDownloadPreviewPng = () => {
    void downloadCertificatePng(adminPreviewModel, "certificate-preview");
  };

  if (!isAdminCertificatesRoute) {
    const loading = myCertsQuery.isLoading;
    const err = myCertsQuery.isError;
    const list = myCertsQuery.data ?? [];

    return (
      <DashboardLayout>
        <div className="dash-page">
          <div className="dash-page-header mb-8">
            <div>
              <h1 className="dash-page-title">My Certificates</h1>
              <p className="dash-page-subtitle">Your earned certificates and achievements.</p>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-16 text-muted-foreground">
              <Loader2 className="animate-spin" size={28} />
            </div>
          ) : err ? (
            <KCard className="p-8 text-center text-destructive">Could not load certificates. Try again later.</KCard>
          ) : list.length === 0 ? (
            <div className="dash-empty-state">
              <Award size={48} className="text-muted-foreground mb-4 mx-auto block" />
              <h3 className="font-bold text-foreground mb-2 text-center">No Certificates Yet</h3>
              <p className="text-sm text-muted-foreground text-center">
                Complete a course and ask your instructor to issue a certificate when you are eligible.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {list.map((cert) => {
                const thumb = templateThumb(cert.template);
                return (
                  <KCard key={cert.id} className="overflow-hidden">
                    {thumb ? (
                      <div className="relative border-b border-border">
                        <img
                          src={thumb}
                          alt="Certificate template"
                          className="w-full object-cover object-top"
                          style={{ maxHeight: 200 }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex flex-col justify-end p-4">
                          <h3 className="text-base font-bold text-white leading-tight">
                            {cert.courseDisplayTitle ?? cert.courseTitle}
                          </h3>
                          <p className="text-sm text-white/90 font-medium">
                            {cert.recipientDisplayName ?? cert.recipientName}
                          </p>
                          {cert.grade ? (
                            <p className="text-xs text-white/80">Grade: {cert.grade}</p>
                          ) : null}
                        </div>
                      </div>
                    ) : (
                      <div
                        className={cn(
                          "bg-gradient-to-br p-8 text-center border-b border-border",
                          previewGradient(cert.template),
                        )}
                      >
                        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                          <Award size={28} className="text-primary" />
                        </div>
                        <p className="text-[10px] tracking-[0.2em] text-muted-foreground uppercase mb-2">Certificate of Completion</p>
                        <h3 className="text-lg font-bold text-foreground mb-1">
                          {cert.courseDisplayTitle ?? cert.courseTitle}
                        </h3>
                        <p className="text-sm text-foreground/90 font-medium mt-1">
                          {cert.recipientDisplayName ?? cert.recipientName}
                        </p>
                        {cert.grade ? <p className="text-sm text-muted-foreground">Grade: {cert.grade}</p> : null}
                        <p className="text-xs text-muted-foreground mt-2">{cert.certificateNumber}</p>
                      </div>
                    )}
                    <div className="p-4 flex items-center justify-between gap-2 flex-wrap">
                      <p className="text-xs text-muted-foreground">Issued: {formatIssuedDate(cert.issuedAt)}</p>
                      <CertificateCardActions cert={cert} />
                    </div>
                  </KCard>
                );
              })}
            </div>
          )}
        </div>
      </DashboardLayout>
    );
  }

  const adminList = adminCertsQuery.data ?? [];
  const coursesLoading = coursesQuery.isLoading;
  const studentsLoading = studentsQuery.isFetching && !!selectedCourseId && pickMode === "roster";

  return (
    <DashboardLayout>
      <div className="dash-page">
        <div className="dash-page-header mb-6">
          <div>
            <h1 className="dash-page-title">Certificate Management</h1>
            <p className="dash-page-subtitle">
              Search students or pick from a course roster. Edit all text below: what you see is what prints on the PNG.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-8">
          <Button
            type="button"
            variant={pickMode === "roster" ? "default" : "secondary"}
            size="sm"
            onClick={() => handlePickMode("roster")}
          >
            By course roster
          </Button>
          <Button
            type="button"
            variant={pickMode === "search" ? "default" : "secondary"}
            size="sm"
            onClick={() => handlePickMode("search")}
          >
            Search students
          </Button>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-8 items-start">
          <div className="space-y-5">
            <h2 className="font-bold text-foreground">Issue certificate</h2>

            {pickMode === "search" ? (
              <div className="space-y-2">
                <Label>Find student (name or email, min. 2 characters)</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
                  <Input
                    className="pl-9"
                    value={globalSearchInput}
                    onChange={(e) => setGlobalSearchInput(e.target.value)}
                    placeholder="Search…"
                  />
                </div>
                {debouncedGlobalSearch.length >= 2 && userSearchQuery.isFetching ? (
                  <p className="text-xs text-muted-foreground flex items-center gap-2">
                    <Loader2 className="animate-spin" size={12} /> Searching…
                  </p>
                ) : null}
                {debouncedGlobalSearch.length >= 2 && userSearchQuery.data && userSearchQuery.data.length > 0 ? (
                  <KCard className="p-0 max-h-48 overflow-y-auto divide-y divide-border">
                    {userSearchQuery.data.map((hit) => (
                      <button
                        key={hit.id}
                        type="button"
                        className={cn(
                          "w-full text-left px-3 py-2 text-sm hover:bg-secondary/80 transition-colors",
                          selectedSearchHit?.id === hit.id && "bg-primary/10",
                        )}
                        onClick={() => selectSearchHit(hit)}
                      >
                        <span className="font-medium text-foreground">{hit.displayName}</span>
                        <span className="block text-xs text-muted-foreground">{hit.email}</span>
                      </button>
                    ))}
                  </KCard>
                ) : null}
                {debouncedGlobalSearch.length >= 2 && userSearchQuery.isFetched && (userSearchQuery.data?.length ?? 0) === 0 ? (
                  <p className="text-xs text-muted-foreground">No students match.</p>
                ) : null}
                {selectedSearchHit ? (
                  <p className="text-xs text-muted-foreground">
                    Selected: <strong className="text-foreground">{selectedSearchHit.displayName}</strong>
                  </p>
                ) : null}
              </div>
            ) : null}

            {pickMode === "roster" ? (
              <>
                <div className="space-y-2">
                  <Label>Course</Label>
                  {coursesLoading ? (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground h-10">
                      <Loader2 className="animate-spin" size={16} /> Loading courses…
                    </div>
                  ) : courses.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No courses available.</p>
                  ) : (
                    <select
                      className="w-full h-10 px-3 rounded-lg border border-border bg-background text-sm text-foreground"
                      value={selectedCourseId}
                      onChange={(e) => setSelectedCourseId(e.target.value)}
                    >
                      {courses.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.title}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Filter roster</Label>
                  <Input
                    value={rosterFilter}
                    onChange={(e) => setRosterFilter(e.target.value)}
                    placeholder="Filter by name or email…"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Student</Label>
                  {!selectedCourseId ? (
                    <p className="text-sm text-muted-foreground">Select a course first.</p>
                  ) : studentsLoading ? (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground h-10">
                      <Loader2 className="animate-spin" size={16} /> Loading students…
                    </div>
                  ) : rosterSelectOptions.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No students match this filter.</p>
                  ) : (
                    <select
                      className="w-full h-10 px-3 rounded-lg border border-border bg-background text-sm text-foreground"
                      value={studentUserId ?? ""}
                      onChange={(e) => setStudentUserId(Number(e.target.value))}
                    >
                      {rosterSelectOptions.map((s) => (
                        <option key={s.userId} value={s.userId}>
                          {displayStudentNameFromRow(s)} ({s.user.email})
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </>
            ) : (
              <div className="space-y-2">
                <Label>Course (their enrollments)</Label>
                {!selectedSearchHit ? (
                  <p className="text-sm text-muted-foreground">Search and select a student first.</p>
                ) : enrollmentsQuery.isLoading ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground h-10">
                    <Loader2 className="animate-spin" size={16} /> Loading courses…
                  </div>
                ) : enrollmentCourses.length === 0 ? (
                  <p className="text-sm text-muted-foreground">This student has no enrollments.</p>
                ) : (
                  <select
                    className="w-full h-10 px-3 rounded-lg border border-border bg-background text-sm text-foreground"
                    value={selectedCourseId}
                    onChange={(e) => setSelectedCourseId(e.target.value)}
                  >
                    {enrollmentCourses.map((e) => (
                      <option key={e.courseId} value={e.courseId}>
                        {e.title}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}

            <div className="space-y-3">
              <Label>Template</Label>
              <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
                {templates.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setSelectedTemplate(t.id)}
                    className={cn(
                      "rounded-xl border-2 transition-all text-center overflow-hidden",
                      selectedTemplate === t.id
                        ? `${t.border} ring-2 ring-offset-1 ring-primary/40`
                        : "border-border hover:border-primary/30",
                    )}
                  >
                    {t.thumbSrc ? (
                      <div className="relative">
                        <img
                          src={t.thumbSrc}
                          alt={t.name}
                          className="w-full h-20 object-cover object-top"
                        />
                        {selectedTemplate === t.id && (
                          <div className="absolute inset-0 bg-primary/10 flex items-center justify-center">
                            <Check size={16} className="text-primary drop-shadow" />
                          </div>
                        )}
                        <p className="text-[10px] font-medium text-foreground py-1 px-1 bg-background">{t.name}</p>
                      </div>
                    ) : (
                      <div className={cn("p-3 bg-gradient-to-br", t.color)}>
                        <Award size={18} className="mx-auto mb-1 text-foreground" />
                        <p className="text-[10px] font-medium text-foreground">{t.name}</p>
                        {selectedTemplate === t.id ? <Check size={12} className="mx-auto mt-0.5 text-primary" /> : null}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Mehul-template–specific fields */}
            {isMehulTemplate(selectedTemplate) && (
              <div className="space-y-3 pt-1 border-t border-border">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Mehul Certificate Fields</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Student ID (学生番号)</Label>
                    <Input
                      value={studentId}
                      onChange={(e) => setStudentId(e.target.value)}
                      placeholder="e.g. 2024001"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Gender 性別</Label>
                    <Input
                      value={gender}
                      onChange={(e) => setGender(e.target.value)}
                      placeholder="e.g. Male / Female"
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Date of Birth 生年月日</Label>
                  <Input
                    value={dateOfBirth}
                    onChange={(e) => setDateOfBirth(e.target.value)}
                    placeholder="e.g. 1995/04/15"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Total Lesson Hours 総授業時間</Label>
                    <Input
                      value={totalLessonHours}
                      onChange={(e) => setTotalLessonHours(e.target.value)}
                      placeholder="e.g. 180"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Attendance % 出席</Label>
                    <Input
                      value={attendance}
                      onChange={(e) => setAttendance(e.target.value)}
                      placeholder="e.g. 100"
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Text Book 教科書</Label>
                  <Input
                    value={textBook}
                    onChange={(e) => setTextBook(e.target.value)}
                    placeholder="e.g. みんなの日本語、ベシック漢字"
                  />
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label>Recipient name (on certificate)</Label>
              <Input
                value={recipientDisplayName}
                onChange={(e) => setRecipientDisplayName(e.target.value)}
                placeholder="Full name as it should appear"
              />
            </div>
            <div className="space-y-2">
              <Label>Course title (on certificate)</Label>
              <Input value={courseDisplayTitle} onChange={(e) => setCourseDisplayTitle(e.target.value)} placeholder="Course name" />
            </div>
            <div className="space-y-2">
              <Label>Institution</Label>
              <Input value={institutionName} onChange={(e) => setInstitutionName(e.target.value)} placeholder={DEFAULT_INSTITUTION} />
            </div>
            <div className="space-y-2">
              <Label>Certificate title</Label>
              <Input value={subtitle} onChange={(e) => setSubtitle(e.target.value)} placeholder={DEFAULT_SUBTITLE} />
            </div>
            <div className="space-y-2">
              <Label>Signatory line (optional)</Label>
              <Input
                value={signatoryTitle}
                onChange={(e) => setSignatoryTitle(e.target.value)}
                placeholder="e.g. Director of Studies"
              />
            </div>
            <div className="space-y-2">
              <Label>Grade (optional)</Label>
              <Input value={gradeInput} onChange={(e) => setGradeInput(e.target.value)} placeholder="e.g. A" maxLength={16} />
            </div>

            <div className="flex flex-wrap gap-2 pt-2">
              <Button
                type="button"
                onClick={handleIssue}
                disabled={
                  !selectedCourseId ||
                  studentUserId == null ||
                  issueMutation.isPending ||
                  (pickMode === "search" && !selectedSearchHit)
                }
              >
                {issueMutation.isPending ? <Loader2 className="animate-spin" size={16} /> : <Award size={16} />}
                Issue & save
              </Button>
              <Button type="button" variant="secondary" onClick={handleDownloadPreviewPng}>
                <Download size={16} /> PNG preview
              </Button>
              <Button type="button" variant="outline" onClick={handlePrintPreview}>
                <Printer size={16} /> Print preview
              </Button>
            </div>
          </div>

          <div className="space-y-3 xl:sticky xl:top-4">
            <h2 className="font-bold text-foreground">Live canvas preview</h2>
            <p className="text-xs text-muted-foreground">Rendered with the HTML canvas (high-resolution PNG export).</p>
            <CertificatePreviewCanvas model={adminPreviewModel} />
          </div>
        </div>

        <div className="mt-12">
          <h2 className="font-bold text-foreground mb-4">Issued certificates</h2>
          {adminCertsQuery.isLoading ? (
            <div className="flex justify-center py-12 text-muted-foreground">
              <Loader2 className="animate-spin" size={28} />
            </div>
          ) : adminCertsQuery.isError ? (
            <KCard className="p-8 text-center text-destructive">Could not load issued certificates.</KCard>
          ) : (
            <KCard className="overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[640px]">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left text-xs font-bold text-muted-foreground uppercase tracking-wider px-6 py-4">Recipient</th>
                      <th className="text-left text-xs font-bold text-muted-foreground uppercase tracking-wider px-6 py-4">Course (on cert)</th>
                      <th className="text-left text-xs font-bold text-muted-foreground uppercase tracking-wider px-6 py-4">Date</th>
                      <th className="text-left text-xs font-bold text-muted-foreground uppercase tracking-wider px-6 py-4">Template</th>
                      <th className="text-left text-xs font-bold text-muted-foreground uppercase tracking-wider px-6 py-4 w-28"> </th>
                    </tr>
                  </thead>
                  <tbody>
                    {adminList.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-6 py-10 text-center text-sm text-muted-foreground">
                          No certificates issued yet.
                        </td>
                      </tr>
                    ) : (
                      adminList.map((c) => (
                        <tr key={c.id} className="border-b border-border last:border-0 hover:bg-secondary/50 transition-colors">
                          <td className="px-6 py-4 text-sm font-semibold text-foreground">
                            {c.recipientDisplayName ?? c.studentName ?? "-"}
                            <span className="block text-xs font-normal text-muted-foreground">{c.studentEmail}</span>
                          </td>
                          <td className="px-6 py-4 text-sm text-muted-foreground">{c.courseDisplayTitle ?? c.courseTitle}</td>
                          <td className="px-6 py-4 text-sm text-muted-foreground">{formatIssuedDate(c.issuedAt)}</td>
                          <td className="px-6 py-4">
                            <span className="text-xs font-bold text-primary bg-primary/10 px-2 py-1 rounded-md">{templateTitle(c.template)}</span>
                          </td>
                          <td className="px-6 py-4 flex gap-1">
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              title="Print"
                              onClick={async () => {
                                const ok = await openCertificatePrintWindow(certDtoToVisual(c));
                                if (!ok) toast.error("Allow pop-ups.");
                              }}
                            >
                              <Printer size={14} />
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              title="PNG"
                              onClick={() => void downloadCertificatePng(certDtoToVisual(c), `certificate-${c.certificateNumber}`)}
                            >
                              <Download size={14} />
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </KCard>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
